/**
 * PDFGenerator.tsx
 * 
 * Servicio principal para la generación de documentos PDF de reportes de muestras.
 * Orquesta los componentes modulares para crear PDFs estructurados.
 * 
 * ARQUITECTURA MODULAR:
 * - PDFComponents: Componentes visuales reutilizables (Header, Title, Tables, etc.)
 * - PDFSections: Secciones complejas (ResultsSection para BT/BR, Sr89/Sr90)
 * - dataProcessing: Lógica de negocio y transformación de datos
 * - sampleTypeStrategies: Patrón Strategy para comportamientos específicos por tipo de muestra
 * 
 * Para más información, consulta: /pdf/docs/README.md
 */

import { Document, Page, PDFDownloadLink, PDFViewer, pdf } from "@react-pdf/renderer";
import { Modal } from "@mui/material";
import React from "react";
import { LabelKeys } from "@app/types/samples.types";

// Importar funciones de procesamiento de datos
import {
  formatOutputValue,
  determineAnalysisType,
  hasBothBTBR,
  hasBothSr89Sr90,
  prepareFormattedData,
  generatePDFFileName,
  extractOutputData,
  isCurrieMethod,
} from "./pdf/dataProcessing";

// Importar componentes de PDF
import {
  styles,
  PDFHeader,
  PDFTitle,
  SampleBasicInfo,
  PreparationInfo,
} from "./pdf/PDFComponents";
import { ResultsSection, OutputDataSections } from "./pdf/PDFSections";

const PDFGeneratorService = {
  downloadPDFDocument: async (
    dataObjects,
    results,
    resultsAll,
    allAnalysisResults?: any
  ) => {
    // ==================== PROCESAMIENTO DE DATOS ====================
    const isCurrier = isCurrieMethod(dataObjects);
    const formattedData = prepareFormattedData(resultsAll);
    const typeAnalysis = determineAnalysisType(dataObjects);
    const currentDate = new Date().toLocaleDateString();
    const nameFile = generatePDFFileName(dataObjects?.sample_registration);

    // Verificar tipos de análisis combinados
    const hasBTBR = hasBothBTBR(dataObjects);
    const hasSr89Sr90 = hasBothSr89Sr90(dataObjects);

    // Extraer datos de salida
    const { arrrayKeys, extendedLabelString } = extractOutputData(
      formattedData,
      isCurrier,
      typeAnalysis?.codigo
    );

    // ==================== GENERACIÓN DEL DOCUMENTO PDF ====================
    const pdfDocument = (
      <Document title={nameFile}>
        <Page size="A4" style={styles.page}>
          {/* Header */}
          <PDFHeader currentDate={currentDate} dataObjects={dataObjects} />

          {/* Title */}
          <PDFTitle
            maslReference={dataObjects?.masl_reference}
            analysisDescription={dataObjects?.typeAnalysis?.descripcion}
          />

          {/* Información Básica de la Muestra */}
          <SampleBasicInfo dataObjects={dataObjects} />

          {/* Información de Preparación y Contaje */}
          <PreparationInfo dataObjects={dataObjects} />

          {/* Sección de Resultados (maneja BT/BR, Sr89/Sr90 y análisis únicos) */}
          <ResultsSection
            hasBothBTBR={hasBTBR}
            hasBothSr89Sr90={hasSr89Sr90}
            allAnalysisResults={allAnalysisResults}
            results={results}
          />

          {/* Sección de Datos de Salida (maneja BT/BR, Sr89/Sr90 y análisis únicos) */}
          <OutputDataSections
            hasBothBTBR={hasBTBR}
            hasBothSr89Sr90={hasSr89Sr90}
            allAnalysisResults={allAnalysisResults}
            dataObjects={dataObjects}
            arrrayKeys={arrrayKeys}
            extendedLabelString={extendedLabelString}
          />
        </Page>
      </Document>
    );

    // ==================== DESCARGA DEL PDF ====================
    const blob = await pdf(pdfDocument).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = nameFile;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  PDFDownload: (document: JSX.Element, fileName: string): JSX.Element => {
    return (
      <PDFDownloadLink document={document} fileName={fileName}>
        Descargar PDF
      </PDFDownloadLink>
    );
  },

  PDFPreview: (document: JSX.Element): JSX.Element => {
    return (
      <Modal title="" open={true} onClose={() => {}}>
        <PDFViewer width={600} height={800}>
          {document}
        </PDFViewer>
      </Modal>
    );
  },
};

export default PDFGeneratorService;
